import React, { useState, useCallback } from 'react';
import {
  Paper, Group, Stack, Grid, Text, Title, Badge, Button,
  Loader, Alert, Select, SimpleGrid
} from '@mantine/core';
import { TextInput } from '@mantine/core';
import { DataGrid } from '@mui/x-data-grid';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis
} from 'recharts';
import { IconRefresh, IconAlertCircle, IconX } from '@tabler/icons-react';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';

const CHART_COLORS = [
  '#1971c2', '#2f9e44', '#e67700', '#7048e8',
  '#c92a2a', '#0c8599', '#5c7cfa', '#f03e3e', '#9c36b5', '#1098ad',
];

const TOOLTIP_STYLE = {
  backgroundColor: 'var(--mantine-color-body)',
  borderColor: 'var(--mantine-color-default-border)',
  color: 'var(--mantine-color-text)',
};
const TOOLTIP_ITEM_STYLE  = { color: 'var(--mantine-color-text)' };
const TOOLTIP_LABEL_STYLE = { color: 'var(--mantine-color-text)' };

// ── chart filter shape: { param, value, label }
// param: 'source' | 'customerCategory' | 'inStock' | 'isKeyCustomer'
// value: string or bool-as-string ('true'/'false')
// label: human-readable display string

function KpiCard({ label, value, sub }) {
  return (
    <Paper withBorder p="md" radius="md" style={{ minWidth: 150 }}>
      <Stack gap={2} align="center">
        <Title order={2}>{value ?? '—'}</Title>
        <Text size="sm" c="dimmed">{label}</Text>
        {sub && <Text size="xs" c="dimmed">{sub}</Text>}
      </Stack>
    </Paper>
  );
}

function SimplePieChart({ data, title, onSliceClick, activeKey }) {
  if (!data?.length) return null;
  const hasActive = activeKey != null;
  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>{title}</Text>
      <ResponsiveContainer width="100%" height={210}>
        <PieChart>
          <Pie
            data={data}
            dataKey="count"
            nameKey="key"
            cx="50%"
            cy="45%"
            outerRadius={70}
            label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
            labelLine={false}
            onClick={(entry) => onSliceClick && onSliceClick(entry)}
            style={{ cursor: onSliceClick ? 'pointer' : 'default' }}
          >
            {data.map((entry, i) => (
              <Cell
                key={i}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
                opacity={!hasActive || entry.key === activeKey ? 1 : 0.3}
                stroke={entry.key === activeKey ? '#fff' : 'none'}
                strokeWidth={entry.key === activeKey ? 2 : 0}
              />
            ))}
          </Pie>
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            itemStyle={TOOLTIP_ITEM_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
            formatter={(v, name) => [v, name]}
          />
          <Legend
            formatter={value => (
              <span style={{ color: 'var(--mantine-color-text)', fontSize: 11 }}>{value}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </Stack>
  );
}

function HorizontalBarChart({ data, title, onBarClick, activeKey }) {
  if (!data?.length) return null;
  const formatted = data.map(d => ({
    count: d.count,
    rawKey: d.key,
    label: d.key
      ? (d.key.length > 32 ? d.key.slice(0, 30) + '…' : d.key)
      : '(Unknown)',
  }));
  const height = Math.max(120, formatted.length * 26 + 20);
  const hasActive = activeKey != null;
  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>{title}</Text>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={formatted} layout="vertical" margin={{ top: 0, right: 32, bottom: 0, left: 0 }}>
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={190}
            tick={{ fontSize: 11, fill: 'var(--mantine-color-dimmed)' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            itemStyle={TOOLTIP_ITEM_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
          />
          <Bar
            dataKey="count"
            radius={[0, 3, 3, 0]}
            onClick={(entry) => onBarClick && onBarClick(entry)}
            style={{ cursor: onBarClick ? 'pointer' : 'default' }}
          >
            {formatted.map((entry, i) => (
              <Cell
                key={i}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
                opacity={!hasActive || entry.rawKey === activeKey ? 1 : 0.3}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Stack>
  );
}

const COLUMNS = [
  { field: 'deal_public_id',     headerName: 'Deal ID',    width: 140 },
  { field: 'buyer_company_name', headerName: 'Customer',   flex: 1, minWidth: 160 },
  {
    field: 'IsAirframeKeyCustomer',
    headerName: 'Key Acct',
    width: 85,
    renderCell: (p) => p.value === 'Y'
      ? <Badge color="blue" variant="light" size="xs">Key</Badge>
      : null,
  },
  { field: 'MarketSegment',      headerName: 'Segment',    width: 130 },
  { field: 'source',             headerName: 'Source',     width: 100 },
  { field: 'line_count',         headerName: 'Lines',      width: 70, type: 'number' },
  {
    field: 'has_part_in_stock', headerName: 'Actionable', width: 110,
    renderCell: (p) => (p.value || p.row.has_alt_part_in_stock)
      ? <Badge color="green" variant="light">Yes</Badge>
      : <Badge color="gray"  variant="light">No</Badge>
  },
  {
    field: 'conversion_status', headerName: 'Status', width: 110,
    renderCell: (p) => {
      const color = { quoted: 'green', no_quote: 'yellow', open: 'gray' }[p.value] ?? 'gray';
      return <Badge color={color} variant="light">{p.value ?? '—'}</Badge>;
    }
  },
  {
    field: 'rfq_create_date', headerName: 'RFQ Date', width: 165,
    renderCell: (p) => p.value ? new Date(p.value).toLocaleString() : '—'
  },
];

const CUSTOMER_COLUMNS = [
  { field: 'key',             headerName: 'Customer',     flex: 1, minWidth: 180 },
  { field: 'marketSegment',   headerName: 'Segment',      width: 140 },
  { field: 'isKeyCustomer',   headerName: 'Key',          width: 60,
    renderCell: (p) => p.value ? <Badge color="blue" variant="light" size="xs">Key</Badge> : null },
  { field: 'rfqCount',        headerName: 'RFQs',         width: 75,  type: 'number' },
  { field: 'respondedCount',  headerName: 'Responded',    width: 100, type: 'number' },
  { field: 'quotedCount',     headerName: 'Quoted',       width: 80,  type: 'number' },
  { field: 'quoteRate',       headerName: 'Quote Rate',   width: 100, type: 'number',
    renderCell: (p) => p.value != null ? `${p.value}%` : '—' },
  { field: 'actionableCount', headerName: 'Actionable',   width: 100, type: 'number' },
  { field: 'actionablePct',   headerName: 'Action %',     width: 90,  type: 'number',
    renderCell: (p) => p.value != null ? `${p.value}%` : '—' },
];

const DARK   = 'var(--mantine-color-dark-7)';
const TEXT   = 'var(--mantine-color-text)';
const BORDER = 'var(--mantine-color-dark-4)';

const gridSx = {
  border: 0,
  '--DataGrid-containerBackground': `${DARK} !important`,
  '--DataGrid-rowBackground':       `${DARK} !important`,
  '--DataGrid-footerBackground':    `${DARK} !important`,
  color: TEXT,
  bgcolor: DARK,
  '&.MuiDataGrid-root .MuiDataGrid-container--top [role=row]': {
    backgroundColor: `${DARK} !important`,
    color: `${TEXT} !important`,
  },
  '& .MuiDataGrid-columnHeaderTitle':  { color: TEXT, fontWeight: 600 },
  '& .MuiDataGrid-columnSeparator':    { color: `${BORDER} !important` },
  '& .MuiDataGrid-filler':             { backgroundColor: `${DARK} !important` },
  '& .MuiDataGrid-row':                { backgroundColor: `${DARK} !important`, color: `${TEXT} !important` },
  '& .MuiDataGrid-row:hover':          { backgroundColor: 'var(--mantine-color-dark-6) !important' },
  '& .MuiDataGrid-cell':               { borderColor: BORDER, color: TEXT, backgroundColor: `${DARK} !important` },
  '& .MuiDataGrid-footerContainer':    { backgroundColor: `${DARK} !important`, borderColor: BORDER, color: TEXT },
  '& .MuiTablePagination-root':        { color: TEXT },
  '& .MuiTablePagination-selectIcon':  { color: TEXT },
  '& .MuiSvgIcon-root':                { color: `${TEXT} !important` },
  '& .MuiDataGrid-overlay':            { backgroundColor: `${DARK} !important`, color: TEXT },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': { outline: 'none !important' },
};

// ── Map a chart click to a chartFilter object ──────────────────────────────
function makeSourceFilter(key)        { return { param: 'source',           value: key,  label: `Source: ${key}` }; }
function makeCustomerCatFilter(key)   { return { param: 'customerCategory', value: key,  label: `Type: ${key}` }; }
function makeActionFilter(key)        {
  const yes = key === 'actionable';
  return { param: 'inStock', value: String(yes), label: yes ? 'Actionable: Yes' : 'Actionable: No' };
}
function makeKeyCustomerFilter(key)   {
  const yes = key === 'Key Account';
  return { param: 'isKeyCustomer', value: String(yes), label: yes ? 'Key Accounts only' : 'Non-Key accounts only' };
}

const toDateStr = (d) => d.toISOString().slice(0, 10);
const defaultFrom = toDateStr(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));
const defaultTo   = toDateStr(new Date());

export default function RFQQuoteTraffic() {
  const [from,           setFrom]          = useState(defaultFrom);
  const [to,             setTo]            = useState(defaultTo);
  const [source,         setSource]        = useState('');
  const [converted,      setConverted]     = useState('');
  const [page,           setPage]          = useState(0);
  const [pageSize,       setPageSize]      = useState(50);
  const [sortModel,      setSortModel]     = useState([{ field: 'rfq_create_date', sort: 'desc' }]);
  const [chartFilter,    setChartFilter]   = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');

  const buildTableUrl = useCallback((cf = chartFilter) => {
    const p = new URLSearchParams({ page, pageSize });
    if (sortModel[0]) { p.set('sortField', sortModel[0].field); p.set('sortDir', sortModel[0].sort); }
    if (from)      p.set('from',      from);
    if (to)        p.set('to',        to);
    if (source)    p.set('source',    source);
    if (converted) p.set('converted', converted);
    if (cf)        p.set(cf.param,    cf.value);
    return `${config.apiBaseUrl}/api/rotabull/opportunities?${p}`;
  }, [from, to, source, converted, page, pageSize, sortModel, chartFilter]);

  const buildSummaryUrl = useCallback(() => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to)   p.set('to',   to);
    return `${config.apiBaseUrl}/api/rotabull/opportunities/summary?${p}`;
  }, [from, to]);

  const [tableUrl,   setTableUrl]   = useState(() => buildTableUrl(null));
  const [summaryUrl, setSummaryUrl] = useState(buildSummaryUrl);

  const { data: tableData,   isLoading: tableLoading, error: tableError, refetch: refetchTable   } = useAuthenticatedData(tableUrl);
  const { data: summaryData, isLoading: summaryLoading,                   refetch: refetchSummary } = useAuthenticatedData(summaryUrl);
  const { data: healthData,                                                refetch: refetchHealth  } = useAuthenticatedData(`${config.apiBaseUrl}/api/rotabull/sync/health`);

  const applyFilters = () => {
    setPage(0);
    setChartFilter(null);
    setTableUrl(buildTableUrl(null));
    setSummaryUrl(buildSummaryUrl());
  };

  const applyChartFilter = (newFilter) => {
    // toggle off if clicking the same item
    const next = chartFilter?.value === newFilter.value && chartFilter?.param === newFilter.param
      ? null
      : newFilter;
    setChartFilter(next);
    setPage(0);
    setTableUrl(buildTableUrl(next));
  };

  const resetChartFilter = () => {
    setChartFilter(null);
    setPage(0);
    setTableUrl(buildTableUrl(null));
  };

  const rows  = (tableData?.rows ?? []).map((r, i) => ({ id: i, ...r }));
  const total = tableData?.total ?? 0;
  const s     = summaryData ?? {};
  const pct   = (n) => n != null && s.total > 0 ? `${Math.round((n / s.total) * 100)}%` : '';

  const syncLabel = () => {
    if (!healthData || healthData.status === 'no_sync_run') return 'Never synced';
    if (!healthData.lastSyncUtc) return 'Never synced';
    const mins = Math.round((Date.now() - new Date(healthData.lastSyncUtc)) / 60_000);
    return `Last sync: ${mins}m ago`;
  };

  // active keys per chart type (for dimming non-selected slices/bars)
  const activeSource    = chartFilter?.param === 'source'           ? chartFilter.value : null;
  const activeCustomer  = chartFilter?.param === 'customerCategory' ? chartFilter.value : null;
  const activeAction    = chartFilter?.param === 'inStock'
    ? (chartFilter.value === 'true' ? 'actionable' : 'not_actionable')
    : null;
  const activeKeyCust   = chartFilter?.param === 'isKeyCustomer'
    ? (chartFilter.value === 'true' ? 'Key Account' : 'Standard')
    : null;

  const hasCharts = s.bySource?.length > 0;

  return (
    <Stack gap="md" p="md">
      {/* Header */}
      <Group justify="space-between">
        <Title order={3}>Rotabull Traffic</Title>
        <Group gap="sm">
          <Badge
            color={healthData?.isStale === false ? 'green' : 'red'}
            variant="outline"
            size="lg"
          >
            {syncLabel()}
          </Badge>
          <Button
            size="xs"
            variant="subtle"
            leftSection={<IconRefresh size={14} />}
            onClick={() => { refetchTable(); refetchSummary(); refetchHealth(); }}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Filters */}
      <Paper withBorder p="sm" radius="md">
        <Group gap="sm" wrap="wrap">
          <TextInput type="date" label="From" size="xs" style={{ width: 150 }}
            value={from} onChange={e => setFrom(e.target.value)} />
          <TextInput type="date" label="To" size="xs" style={{ width: 150 }}
            value={to} onChange={e => setTo(e.target.value)} />
          <Select label="Source" size="xs" clearable style={{ width: 140 }}
            value={source || null} onChange={v => setSource(v ?? '')}
            data={['ILS', 'Email', 'StockMarket', 'The145', 'SkySelect', 'Partsbase']} />
          <Select label="Status" size="xs" clearable style={{ width: 130 }}
            value={converted || null} onChange={v => setConverted(v ?? '')}
            data={[
              { value: 'open',     label: 'Open' },
              { value: 'quoted',   label: 'Quoted' },
              { value: 'no_quote', label: 'No Quote' },
            ]} />
          <Button size="xs" style={{ alignSelf: 'flex-end' }} onClick={applyFilters}>
            Apply
          </Button>
        </Group>
      </Paper>

      {/* KPI Cards */}
      {summaryLoading ? (
        <Group justify="center"><Loader size="sm" /></Group>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          <KpiCard label="Total Opportunities"  value={s.total?.toLocaleString()} />
          <KpiCard label="Actionable"           value={s.actionableCount?.toLocaleString()}  sub={pct(s.actionableCount)} />
          <KpiCard label="Quoted"               value={s.quotedCount?.toLocaleString()}      sub={pct(s.quotedCount)} />
          <KpiCard label="No Quote"             value={s.noQuoteCount?.toLocaleString()}     sub={pct(s.noQuoteCount)} />
        </SimpleGrid>
      )}

      {/* Price Quality */}
      {s.priceQuality && (
        <Paper withBorder p="sm" radius="md">
          <Stack gap="xs">
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">Price Quality</Text>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
              <KpiCard
                label="Parts Priced"
                value={`${s.priceQuality.partsWithPrice?.toLocaleString()} / ${s.priceQuality.distinctParts?.toLocaleString()}`}
                sub={s.priceQuality.distinctParts > 0
                  ? `${Math.round((s.priceQuality.partsWithPrice / s.priceQuality.distinctParts) * 100)}%`
                  : ''}
              />
              <KpiCard
                label="Expired Prices"
                value={s.priceQuality.partsExpired?.toLocaleString()}
                sub={s.priceQuality.distinctParts > 0
                  ? `${Math.round((s.priceQuality.partsExpired / s.priceQuality.distinctParts) * 100)}% of parts`
                  : ''}
              />
              <KpiCard
                label="Avg Price Age"
                value={s.priceQuality.avgPriceAgeDays != null
                  ? `${Math.round(s.priceQuality.avgPriceAgeDays)}d`
                  : '—'}
              />
            </SimpleGrid>
          </Stack>
        </Paper>
      )}

      {/* Charts */}
      {hasCharts && (
        <Paper withBorder p="md" radius="md">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SimplePieChart
                data={s.bySource}
                title="By Source"
                activeKey={activeSource}
                onSliceClick={(entry) => applyChartFilter(makeSourceFilter(entry.name))}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SimplePieChart
                data={s.byActionability}
                title="Actionability"
                activeKey={activeAction}
                onSliceClick={(entry) => applyChartFilter(makeActionFilter(entry.name))}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SimplePieChart
                data={s.byKeyCustomer}
                title="Key Accounts"
                activeKey={activeKeyCust}
                onSliceClick={(entry) => applyChartFilter(makeKeyCustomerFilter(entry.name))}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SimplePieChart
                data={s.byCondition}
                title="By Condition (Lines)"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SimplePieChart
                data={s.byPartClass}
                title="By Part Class (Lines)"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <SimplePieChart
                data={s.byPartInSystem}
                title="Part in ERP System?"
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <HorizontalBarChart
                data={s.byCustomerCategory}
                title="Customer Type"
                activeKey={activeCustomer}
                onBarClick={(entry) => applyChartFilter(makeCustomerCatFilter(entry.rawKey))}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <HorizontalBarChart
                data={s.byPlatform?.slice(0, 15)}
                title="By Platform (Dominant)"
              />
            </Grid.Col>
          </Grid>
        </Paper>
      )}

      {/* Airline vs Other breakdown */}
      {s.byCustomerCategoryDeals?.length > 0 && (
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">By Customer Type</Text>
            <Grid>
              {s.byCustomerCategoryDeals.map(d => {
                const lines = s.byCustomerCategoryLines?.find(l => l.category === d.category) ?? {};
                const isAirline = d.category === 'Airline';
                return (
                  <Grid.Col key={d.category} span={{ base: 12, sm: 6 }}>
                    <Paper withBorder p="sm" radius="sm" bg={isAirline ? 'var(--mantine-color-blue-light)' : undefined}>
                      <Stack gap="xs">
                        <Group gap="xs">
                          <Text fw={600} size="sm">{d.category}</Text>
                          {isAirline && <Badge color="blue" variant="filled" size="xs">Simulation Scope</Badge>}
                        </Group>
                        <SimpleGrid cols={2} spacing="xs">
                          <Stack gap={1} align="center">
                            <Text fw={700} size="lg">{d.total?.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">RFQs</Text>
                          </Stack>
                          <Stack gap={1} align="center">
                            <Text fw={700} size="lg">{d.actionable?.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">Actionable</Text>
                          </Stack>
                          <Stack gap={1} align="center">
                            <Text fw={700} size="lg">{lines.inStock?.toLocaleString() ?? '—'}</Text>
                            <Text size="xs" c="dimmed">Lines In-Stock</Text>
                          </Stack>
                          <Stack gap={1} align="center">
                            <Text fw={700} size="lg">{lines.priced?.toLocaleString() ?? '—'}</Text>
                            <Text size="xs" c="dimmed">Lines Priced</Text>
                          </Stack>
                        </SimpleGrid>
                        <Text size="xs" c="dimmed" ta="right">
                          {d.quoted?.toLocaleString()} quoted · {d.noQuote?.toLocaleString()} no-quote
                        </Text>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                );
              })}
            </Grid>
          </Stack>
        </Paper>
      )}

      {/* Traffic by Customer */}
      {s.byTopCustomer?.length > 0 && (
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Text size="xs" fw={600} tt="uppercase" c="dimmed">Traffic by Customer</Text>
              <Group gap="sm">
                {s.distinctCustomers != null && (
                  <Badge variant="outline" color="gray" size="sm">
                    {s.distinctCustomers.toLocaleString()} customers
                  </Badge>
                )}
                <TextInput
                  placeholder="Search company…"
                  size="xs"
                  style={{ width: 220 }}
                  value={customerSearch}
                  onChange={e => setCustomerSearch(e.target.value)}
                />
              </Group>
            </Group>
            <div style={{ height: 400 }}>
              <DataGrid
                rows={(s.byTopCustomer ?? [])
                  .filter(r => !customerSearch || r.key?.toLowerCase().includes(customerSearch.toLowerCase()))
                  .map((r, i) => ({ id: i, ...r }))}
                columns={CUSTOMER_COLUMNS}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                disableRowSelectionOnClick
                sx={gridSx}
                style={{ height: '100%' }}
              />
            </div>
          </Stack>
        </Paper>
      )}

      {/* Selection banner */}
      {chartFilter && (
        <Paper withBorder p="xs" radius="md" bg="var(--mantine-color-blue-light)">
          <Group gap="xs" align="center">
            <Text size="sm" fw={500}>Filtered by:</Text>
            <Badge variant="filled" size="md" color="blue">{chartFilter.label}</Badge>
            <Button
              size="xs"
              variant="subtle"
              color="blue"
              leftSection={<IconX size={12} />}
              onClick={resetChartFilter}
            >
              Reset
            </Button>
          </Group>
        </Paper>
      )}

    </Stack>
  );
}
